/**
 * The 60-scope permission catalog and default preset matrix.
 *
 * SOURCE OF TRUTH — every guard, every TaskUserRights computation, every custom-role UI
 * checkbox reads from here. If you change anything, the matrix tests in
 * `apps/api/src/modules/permissions/permissions.matrix.spec.ts` and
 * `task-rights.matrix.spec.ts` (Phase 2) regenerate from this file.
 *
 * See docs/08-PERMISSIONS-AND-CUSTOMIZATION.md for the human-readable doc.
 */
import type { PresetRole } from './roles';

export const PERMISSION_SCOPES = [
  // team.*  (7)
  'team.view',
  'team.edit',
  'team.delete',
  'team.transfer_ownership',
  'team.manage_billing',
  'team.manage_integrations',
  'team.manage_workflow',

  // member.*  (4)
  'member.view',
  'member.invite',
  'member.remove',
  'member.change_role',

  // project.*  (10)
  'project.view_all',
  'project.create',
  'project.edit_any',
  'project.edit_own',
  'project.archive',
  'project.delete',
  'project.manage_access',
  'project.manage_workflow',
  'project.manage_integrations',
  'project.manage_billing',

  // task.*  (18)
  'task.view',
  'task.create',
  'task.edit_any',
  'task.edit_as_creator',
  'task.edit_as_respondent',
  'task.edit_as_assignee',
  'task.assign',
  'task.set_respondent',
  'task.change_status_any',
  'task.change_status_as_assignee',
  'task.change_status_as_respondent',
  'task.change_status_to_complete',
  'task.delete',
  'task.delete_permanently',
  'task.archive',
  'task.manage_relations',
  'task.manage_milestone',
  'task.comment',

  // time.*  (6)
  'time.log_own',
  'time.log_any',
  'time.view_own',
  'time.view_team',
  'time.view_billing',
  'time.export_reports',

  // channel.*  (5)
  'channel.view',
  'channel.post',
  'channel.create',
  'channel.archive',
  'channel.manage_members',

  // file.*  (4)
  'file.upload',
  'file.delete_own',
  'file.delete_any',
  'file.manage_folders',

  // page.*  (1) — wiki authoring (team handbook + project docs). Create / edit /
  // delete / restore pages. Reading is open to any team member; this gates only
  // writes, so a read-only VIEWER can browse but never edit or delete docs.
  'page.edit',

  // integration.*  (5)
  'github.view',
  'github.manage_link',
  'ai.use',
  // OWNER-only: grants the holder's PATs the right to call MCP write
  // tools (knowledge.upsert_chunks today; agent write tools in Phase 3).
  // Customers leave this off regular roles — only their AI Core service
  // PAT carries it.
  'ai.mcp_act',
  // Phase 2 AI Assistant — grants access to the chat panel that grounds
  // answers in workspace content via RAG.  Separate from the umbrella
  // `ai.use` so ops can grant chat-only access without unlocking the
  // single-shot LLM features (parse-task / digest / co-pilot).
  'ai.use_assistant_chat',
  // Phase 3 — install / configure / promote-demote / disable agents on
  // the team.  OWNER-only by default — agents act under user identity
  // (via ephemeral PAT), so the privilege bar is "can hire a teammate".
  // Surfaced in the custom-role UI so a team can delegate this to a
  // designated AI ops role without granting full OWNER.
  'ai.agent_admin',
  // Phase 3 — PAT-only scope granted to AI Core agent runtime service
  // PATs.  Required to call MCP write tools that drive agent runs
  // (agent.run.start / append_step / complete / fail) + the agent
  // action tools (task.add_comment by agent, etc.).  Mirror of
  // `ai.mcp_act` for knowledge writes — never granted to a regular
  // role's PAT; only minted onto the AI Core service PAT.
  'ai.agent_act',

  // governance.*  (2) — Phase 4 (Trust & Governance)
  // Manage the Approval Center — create approvals on behalf of others,
  // resolve approvals as a system actor, configure escalation policies.
  // OWNER + MANAGER by default; surfaced in the custom-role UI so teams
  // can delegate to a dedicated Compliance / Ops role without granting
  // full OWNER.  Decisions on individual approvals do NOT require this
  // scope — being listed in `required_approvers` is the gate.
  'governance.approval_admin',
  // Manage the Policy Center — create / edit / enable named policies
  // that govern AI behaviour, approval routing, notification gating,
  // workflow constraints.  OWNER + MANAGER by default.  Reading
  // existing policies does NOT require this scope — governance is
  // transparent by default for team members.
  'governance.policy_admin',

  // workflow.*  (1) — Phase 5 (Workflows + Incidents)
  // Author + edit per-team / per-project custom workflows: the state
  // graph that drives task status transitions.  OWNER + MANAGER + LEAD
  // by default (LEAD because workflow ownership typically rolls up to
  // engineering leads, not just team admins).  Reading existing
  // workflows does NOT require this scope.
  'workflow.admin',
] as const;

export type PermissionScope = (typeof PERMISSION_SCOPES)[number];

/** Scopes excluded from the custom-role checkbox UI — OWNER-only. */
export const OWNER_ONLY_SCOPES: ReadonlySet<PermissionScope> = new Set([
  'team.delete',
  'team.transfer_ownership',
  'team.manage_billing',
  // Granted only to PATs issued for the AI Core ingest worker — never
  // exposed in the custom-role UI.
  'ai.mcp_act',
  // Same logic — granted only to the AI Core agent runtime service
  // PAT, never to a regular role's PAT.
  'ai.agent_act',
]);

/** Logical groups for the custom-role UI. Order is significant. */
export const PERMISSION_GROUPS: ReadonlyArray<{
  key: string;
  label: string;
  scopes: ReadonlyArray<PermissionScope>;
}> = [
  {
    key: 'team',
    label: 'Team',
    scopes: [
      'team.view',
      'team.edit',
      'team.delete',
      'team.transfer_ownership',
      'team.manage_billing',
      'team.manage_integrations',
      'team.manage_workflow',
    ],
  },
  {
    key: 'member',
    label: 'Members',
    scopes: ['member.view', 'member.invite', 'member.remove', 'member.change_role'],
  },
  {
    key: 'project',
    label: 'Projects',
    scopes: [
      'project.view_all',
      'project.create',
      'project.edit_any',
      'project.edit_own',
      'project.archive',
      'project.delete',
      'project.manage_access',
      'project.manage_workflow',
      'project.manage_integrations',
      'project.manage_billing',
    ],
  },
  {
    key: 'task',
    label: 'Tasks',
    scopes: [
      'task.view',
      'task.create',
      'task.edit_any',
      'task.edit_as_creator',
      'task.edit_as_respondent',
      'task.edit_as_assignee',
      'task.assign',
      'task.set_respondent',
      'task.change_status_any',
      'task.change_status_as_assignee',
      'task.change_status_as_respondent',
      'task.change_status_to_complete',
      'task.delete',
      'task.delete_permanently',
      'task.archive',
      'task.manage_relations',
      'task.manage_milestone',
      'task.comment',
    ],
  },
  {
    key: 'time',
    label: 'Time tracking',
    scopes: [
      'time.log_own',
      'time.log_any',
      'time.view_own',
      'time.view_team',
      'time.view_billing',
      'time.export_reports',
    ],
  },
  {
    key: 'channel',
    label: 'Channels',
    scopes: ['channel.view', 'channel.post', 'channel.create', 'channel.archive', 'channel.manage_members'],
  },
  {
    key: 'file',
    label: 'Files',
    scopes: ['file.upload', 'file.delete_own', 'file.delete_any', 'file.manage_folders'],
  },
  {
    key: 'page',
    label: 'Wiki & Docs',
    scopes: ['page.edit'],
  },
  {
    key: 'integration',
    label: 'Integrations',
    scopes: ['github.view', 'github.manage_link'],
  },
  {
    key: 'ai',
    label: 'AI & Agents',
    scopes: [
      // Who may use AI at all, the assistant chat specifically, MCP writes
      // (OWNER-only — hidden in the role UI), and who may install / configure /
      // promote agents.  Agents always act under the triggering user's own
      // permissions, so granting `ai.agent_admin` controls *configuration*, not
      // the agent's reach.
      'ai.use',
      'ai.use_assistant_chat',
      'ai.mcp_act',
      'ai.agent_admin',
    ],
  },
  {
    key: 'governance',
    label: 'Governance',
    scopes: [
      // governance.approval_admin gates who can approve agent / bulk actions.
      'governance.approval_admin',
      'governance.policy_admin',
      'workflow.admin',
    ],
  },
];

/**
 * Default permission matrix for the six preset roles.
 *
 * MEMBER-with-task-roles and OUTSTAFF-self-only behaviours are computed at evaluation time
 * (see `task-rights.service` — Phase 2). This matrix represents the *team-role* layer only.
 */
export const PRESET_PERMISSIONS: Readonly<Record<PresetRole, ReadonlySet<PermissionScope>>> = {
  OWNER: new Set(PERMISSION_SCOPES),

  MANAGER: new Set<PermissionScope>([
    'team.view',
    'team.edit',
    'team.manage_integrations',
    'team.manage_workflow',
    'member.view',
    'member.invite',
    'member.remove',
    'member.change_role',
    'project.view_all',
    'project.create',
    'project.edit_any',
    'project.edit_own',
    'project.archive',
    'project.manage_access',
    'project.manage_workflow',
    'project.manage_integrations',
    'project.manage_billing',
    'task.view',
    'task.create',
    'task.edit_any',
    'task.edit_as_creator',
    'task.edit_as_respondent',
    'task.edit_as_assignee',
    'task.assign',
    'task.set_respondent',
    'task.change_status_any',
    'task.change_status_as_assignee',
    'task.change_status_as_respondent',
    'task.change_status_to_complete',
    'task.delete',
    'task.archive',
    'task.manage_relations',
    'task.manage_milestone',
    'task.comment',
    'time.log_own',
    'time.log_any',
    'time.view_own',
    'time.view_team',
    'time.view_billing',
    'time.export_reports',
    'channel.view',
    'channel.post',
    'channel.create',
    'channel.archive',
    'channel.manage_members',
    'file.upload',
    'file.delete_own',
    'file.delete_any',
    'file.manage_folders',
    'page.edit',
    'github.view',
    'github.manage_link',
    'ai.use',
    'ai.agent_admin',
    'governance.approval_admin',
    'governance.policy_admin',
    'workflow.admin',
  ]),

  LEAD: new Set<PermissionScope>([
    'team.view',
    'team.manage_workflow',
    'member.view',
    'member.invite',
    'project.view_all',
    'project.create',
    'project.edit_own',
    'project.archive',
    'project.manage_access',
    'project.manage_workflow',
    'project.manage_integrations',
    'task.view',
    'task.create',
    'task.edit_any',
    'task.edit_as_creator',
    'task.edit_as_respondent',
    'task.edit_as_assignee',
    'task.assign',
    'task.set_respondent',
    'task.change_status_any',
    'task.change_status_as_assignee',
    'task.change_status_as_respondent',
    'task.change_status_to_complete',
    'task.delete',
    'task.archive',
    'task.manage_relations',
    'task.manage_milestone',
    'task.comment',
    'time.log_own',
    'time.view_own',
    'time.view_team',
    'channel.view',
    'channel.post',
    'channel.create',
    'channel.archive',
    'channel.manage_members',
    'file.upload',
    'file.delete_own',
    'file.manage_folders',
    'page.edit',
    'github.view',
    'github.manage_link',
    'ai.use',
    'workflow.admin',
  ]),

  MEMBER: new Set<PermissionScope>([
    'team.view',
    'member.view',
    'project.view_all',
    'task.view',
    'task.create',
    'task.edit_as_creator',
    'task.edit_as_respondent',
    'task.edit_as_assignee',
    'task.assign',
    'task.change_status_as_assignee',
    'task.change_status_as_respondent',
    'task.manage_relations',
    'task.manage_milestone',
    'task.comment',
    'time.log_own',
    'time.view_own',
    'channel.view',
    'channel.post',
    'channel.create',
    'file.upload',
    'file.delete_own',
    'page.edit',
    'github.view',
    'ai.use',
  ]),

  OUTSTAFF: new Set<PermissionScope>([
    'team.view',
    'member.view',
    'task.view', // self-only filter applied server-side
    'task.edit_as_respondent',
    'task.edit_as_assignee',
    'task.change_status_as_assignee',
    'task.change_status_as_respondent',
    'task.comment', // own task only — gated by RLS / self-filter
    'time.log_own',
    'time.view_own',
    'channel.view',
    'channel.post',
    'file.upload',
    'file.delete_own',
    'page.edit',
    'github.view', // own commits/PRs only
  ]),

  VIEWER: new Set<PermissionScope>([
    'team.view',
    'member.view',
    'project.view_all', // read-only, restricted by project_access
    'task.view',
    'task.comment', // observers may leave comments — see docs/22 §3.9
    'channel.view',
  ]),
};

/** Convenience accessor — returns a fresh Set every call (do not mutate the result). */
export function presetPermissions(role: PresetRole): Set<PermissionScope> {
  return new Set(PRESET_PERMISSIONS[role]);
}
